import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import DocumentPreview from './DocumentPreview';

function CertificateVerificationCard({ certificate, onApprove, onReject }) {
  const [isExpanding, setIsExpanding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState('');

  const handleApprove = async () => {
    setIsLoading(true);
    try {
      await onApprove(certificate.id);
    } catch (error) {
      console.error("Failed to approve certificate:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    setIsLoading(true);
    try {
      await onReject(certificate.id, reason);
      setReason('');
    } catch (error) {
      console.error("Failed to reject certificate:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Certificate #{certificate.id.substring(0, 8)}
            </h3>
            {certificate.status && (
              <span className={
                `px-2 py-0.5 text-xs font-medium rounded-full ${
                  certificate.status === 'approved' ? 'bg-green-100 text-green-800' : 
                  certificate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                  certificate.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                  'bg-gray-100 text-gray-800'
                }`
              }>
                {certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)}
              </span>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Submitted on {certificate.submittedAt ? format(parseISO(certificate.submittedAt), 'MMM dd, yyyy') : 'Unknown date'}
          </p>
        </div>
        
        {/* Only show approve/reject buttons for pending certificates */}
        {(!certificate.status || certificate.status === 'pending') ? (
          <div className="flex space-x-2">
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white 
              ${isLoading ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'} 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
              onClick={handleApprove}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Approve'}
            </button>
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white 
              ${isLoading ? 'bg-red-400' : 'bg-red-600 hover:bg-red-700'} 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
              onClick={() => setIsExpanding(!isExpanding)}
              disabled={isLoading}
            >
              Reject
            </button>
          </div>
        ) : (
          <div>
            <span className="text-sm text-gray-500">{certificate.status === 'approved' ? 'Certificate verified' : 'Certificate rejected'}</span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Donor Name</dt>
            <dd className="mt-1 text-sm text-gray-900">{certificate.donorName}</dd>
          </div>
          
          {/* Certificate Preview */}
          {certificate.fileUrl && (
            <div className="sm:col-span-2 mt-4">
              <dt className="text-sm font-medium text-gray-500 mb-2">Certificate Document</dt>
              <dd>
                <DocumentPreview 
                  fileUrl={certificate.fileUrl} 
                  altText={`${certificate.donorName}'s Certificate`} 
                />
              </dd>
            </div>
          )}
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Organization</dt>
            <dd className="mt-1 text-sm text-gray-900">{certificate.organization}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Certificate Type</dt>
            <dd className="mt-1 text-sm text-gray-900">{certificate.type}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(certificate.expiryDate)}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-sm font-medium text-gray-500">Notes</dt>
            <dd className="mt-1 text-sm text-gray-900">{certificate.notes || 'No notes provided'}</dd>
          </div>
        </dl>
      </div>

      {/* Certificate preview */}
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
        <dt className="text-sm font-medium text-gray-500 mb-2">Certificate Image</dt>
        <div className="flex justify-center bg-gray-50 rounded-md p-2">
          <a 
            href={certificate.fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="relative"
          >
            <img 
              src={certificate.fileUrl} 
              alt="Certificate" 
              className="max-h-48 object-contain rounded"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity duration-200">
              <span className="text-white opacity-0 hover:opacity-100 text-xs font-medium px-2 py-1 bg-gray-900 bg-opacity-75 rounded">
                Click to view full size
              </span>
            </div>
          </a>
        </div>
      </div>

      {/* Rejection form */}
      {isExpanding && (
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6 bg-red-50">
          <h4 className="text-sm font-medium text-red-700 mb-2">Rejection Reason</h4>
          <textarea
            rows="2"
            className="shadow-sm block w-full focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
            placeholder="Provide a reason for rejection..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          ></textarea>
          <div className="mt-3 flex justify-end space-x-2">
            <button
              type="button"
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setIsExpanding(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={handleReject}
              disabled={isLoading || reason.trim() === ''}
            >
              {isLoading ? 'Processing...' : 'Submit Rejection'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificateVerificationCard;
